import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Save, Plus, Trash2, Globe } from 'lucide-react';
import { Button } from '../ui/button';

export default function ViewSelector({
    views = [],
    activeViewId,
    isDirty,
    onSwitchView,
    onSaveView,
    onSaveAsView,
    onDeleteView,
    onPushToOrg,
    isAdmin,
}) {
    const [open, setOpen] = useState(false);
    const [showSaveAs, setShowSaveAs] = useState(false);
    const [newName, setNewName] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const activeView = views.find(v => v.view_id === activeViewId);

    const handleSaveAs = () => {
        if (newName.trim()) {
            onSaveAsView(newName.trim());
            setNewName('');
            setShowSaveAs(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            {/* View selector dropdown */}
            <div className="relative" ref={ref}>
                <button
                    onClick={() => setOpen(!open)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-100 rounded-lg transition-colors"
                >
                    {activeView?.scope === 'org' && <Globe className="w-3.5 h-3.5 text-stone-400" />}
                    {activeView?.name || 'All Companies'}
                    <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
                </button>

                {open && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg border border-stone-200 shadow-lg z-50 py-1">
                        {views.map(v => (
                            <button
                                key={v.view_id}
                                onClick={() => { onSwitchView(v.view_id); setOpen(false); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors text-left"
                            >
                                {v.view_id === activeViewId && <Check className="w-3.5 h-3.5 text-stone-900" />}
                                <span className={v.view_id === activeViewId ? 'font-medium' : ''}>{v.name}</span>
                                {v.scope === 'org' && <Globe className="w-3 h-3 text-stone-400 ml-auto" />}
                            </button>
                        ))}
                        {views.length === 0 && (
                            <p className="text-xs text-stone-400 text-center py-2">No saved views</p>
                        )}
                        <div className="border-t border-stone-100 mt-1 pt-1">
                            <button
                                onClick={() => { setShowSaveAs(true); setOpen(false); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-500 hover:bg-stone-50 transition-colors"
                            >
                                <Plus className="w-3 h-3" /> Save current as new view
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Save button */}
            {isDirty && activeViewId && (
                <Button variant="outline" size="sm" onClick={onSaveView} className="text-xs h-7">
                    <Save className="w-3 h-3 mr-1" /> Save
                </Button>
            )}

            {/* Admin push to org */}
            {isAdmin && activeViewId && activeView?.scope === 'personal' && (
                <Button variant="ghost" size="sm" onClick={() => onPushToOrg(activeViewId)} className="text-xs h-7 text-stone-400">
                    <Globe className="w-3 h-3 mr-1" /> Push to org
                </Button>
            )}

            {/* Save As Modal (inline) */}
            {showSaveAs && (
                <div className="flex items-center gap-2">
                    <input
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSaveAs()}
                        placeholder="View name..."
                        className="px-2 py-1 text-xs border border-stone-200 rounded-md w-32 focus:outline-none focus:ring-1 focus:ring-stone-400"
                        autoFocus
                    />
                    <Button size="sm" onClick={handleSaveAs} className="text-xs h-7">Save</Button>
                    <button onClick={() => setShowSaveAs(false)} className="text-xs text-stone-400 hover:text-stone-600">Cancel</button>
                </div>
            )}
        </div>
    );
}
